"use client";

import DomPurify from "isomorphic-dompurify";
import { CornerRightDown } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { FadeIn } from "components/ui/FadeIn";
import { ButtonVariant } from "components/ui/DualButtonCTA";
import { Button, ButtonVariantTypes, getButtonVariantStylesAndSize } from "components/ui/button";

interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant?: ButtonVariant;
}

interface HeroProps {
  bgColor?: string;
  textColor?: string;
  miniTitle?: string;
  miniTitleColor?: string;
  header: string;
  subHeader: string;
  body?: string;
  bodyTextColor?: string;
  button1?: ButtonProps;
  button2?: ButtonProps;
  showLearn?: boolean;
  className?: string;
  titleTag?: React.ElementType;
  subHeaderTag?: React.ElementType;
  headerTextColor?: string;
  subHeaderTextColor?: string;
  backgroundImage?: {
    src: string;
    alt: string;
  };
  glowEffect?: boolean;
  fullOverlay?: boolean;
  isTop?: boolean;
  isTall?: boolean;
}

const Hero: React.FC<HeroProps> = ({
  bgColor = "",
  textColor = "#073937",
  miniTitle,
  miniTitleColor,
  header,
  subHeader,
  body,
  bodyTextColor,
  button1,
  button2,
  showLearn = false,
  className = "",
  titleTag: TitleTag = "h1",
  subHeaderTag: SubHeaderTag = "h2",
  headerTextColor = "#073937",
  subHeaderTextColor = "#073937",
  backgroundImage,
  glowEffect = false,
  fullOverlay = false,
  isTop = false,
  isTall = false
}) => {
  return (
    <section
      id="hero-section"
      style={{ background: bgColor }}
      className={`${isTop ? "intro-image -mt-[82px]" : ""} intro-image relative @container ${className}`}
    >
      {isTop && <div className={`bnbb ${fullOverlay ? "lgs" : ""}`}></div>}
      <div className={`relative ${isTall ? "h-[90vh]" : "h-[750px]"} w-full`}>
        {backgroundImage && (
          <Image
            src={backgroundImage.src}
            alt={backgroundImage.alt}
            quality={90}
            fill
            priority
            style={{
              objectFit: "cover",
              objectPosition: "50% 62%"
            }}
          />
        )}
        <div className="relative z-20 m-auto flex h-full items-center justify-center px-6 py-20 @2xl:px-28 @2xl:py-12">
          <FadeIn className="mx-auto max-w-[600px] text-center">
            {miniTitle && (
              <p className="mb-4 text-center text-[18px] font-semibold" style={{ color: miniTitleColor || textColor }}>
                {miniTitle}
              </p>
            )}
            <TitleTag
              className="mb-6 text-center font-heading text-[42px] leading-[48px] [text-wrap:balance] lg:text-[52px] lg:leading-[64px]"
              style={{ color: headerTextColor }}
              dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(header) }}
            />
            <SubHeaderTag
              className={`${
                glowEffect ? "smallglow" : ""
              } relative mx-auto mb-12 mt-6 max-w-3xl px-12 text-lg leading-8`}
              style={{ color: subHeaderTextColor }}
              dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(subHeader) }}
            />
            {body && (
              <p className="mb-8 text-center text-md [text-wrap:balance] lg:text-lg" style={{ color: bodyTextColor }}>
                {body}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-4">
              {button1 && (
                <Link href={button1.buttonUrl} passHref>
                  <button
                    className={getButtonVariantStylesAndSize({
                      variant: (button1.buttonVariant as ButtonVariantTypes) || ButtonVariantTypes.BRAND,
                      size: "xl"
                    })}
                    title={button1.buttonText}
                  >
                    {button1.buttonText}
                  </button>
                </Link>
              )}
              {button2 && (
                <Link href={button2.buttonUrl} passHref>
                  <Button
                    variant={(button2.buttonVariant as ButtonVariantTypes) || ButtonVariantTypes.LINK_ICON_ARROW}
                    size="xl"
                  >
                    {button2.buttonText}
                  </Button>
                </Link>
              )}
            </div>
            {showLearn && (
              <div className="mt-8 text-center">
                <Button
                  variant={ButtonVariantTypes.LINK_ICON_ARROW}
                  className="ml-6 items-center justify-center"
                  icon={<CornerRightDown size={20} className="corner-right-down-icon ml-2 mt-[10px]" strokeWidth={2} />}
                >
                  <Link href="#atom-layout-69"> Or Learn More</Link>
                </Button>
              </div>
            )}
          </FadeIn>
        </div>
      </div>
      <div className="hero--coverrrr relative z-0"></div>
    </section>
  );
};

export default Hero;
